import { Copy05 } from '@untitled-ui/icons-react/build/cjs';
import { debounce } from 'lodash';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import BadgeCmp from 'src/components/BadgeCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import { getBadgeLabelByQuoteStatus, getBadgeTypeByQuoteStatus, STATUS } from 'src/constants/common';
import WalToast from 'src/utils/WalToast';

/**
 * SharedQuoteHeader — read-only title bar for the public share-link page.
 *
 * Mirrors the auth QuoteDetail header: "#<quote code> <status badge>" plus a
 * copy-to-clipboard affordance. Strips the auth-only chrome — back button,
 * edit/archive action dropdown, and the connected-users avatar group are all
 * gone since share viewers are anonymous.
 */

interface IProps {
    code?: string | null;
    status?: string | null;
    /** Promoted to ACTIVE when there are active carrier quotes still selectable. */
    hasActiveQuotes?: boolean;
    isLoading?: boolean;
}

const SharedQuoteHeader: React.FC<IProps> = ({ code, status, hasActiveQuotes, isLoading }) => {
    const { t } = useTranslation();

    const copyDebounce = useCallback(
        debounce((value: string) => {
            if (!value) return;
            navigator.clipboard.writeText(value);
            WalToast.copy(t('Common.Copied_to_clipboard'), '', 'copy-msg');
        }, 0),
        []
    );

    // Mirror the auth page rule: a quote that's still in pre-booked states but
    // has active carrier offers is shown as ACTIVE in the badge.
    const effectiveStatus = hasActiveQuotes && status !== STATUS.BOOKED ? STATUS.ACTIVE : status;

    return (
        <div className="flex items-center gap-1.5 lg:gap-2">
            <h1 className={`text-grayLight900 text-sm lg:text-lg font-semibold ${!code && isLoading ? 'custom-loading' : ''}`}>{code ? `#${code}` : isLoading ? 'SQ-NK-200352' : ''}</h1>

            {code && (
                <TooltipCmp message={t('Common.Copy')}>
                    <Copy05 className="h-4 w-4 lg:w-5 lg:h-5 text-primary cursor-pointer" onClick={() => copyDebounce(code)} />
                </TooltipCmp>
            )}

            {effectiveStatus && !isLoading && (
                <BadgeCmp style="modern" mainClassName="bg-white" type={getBadgeTypeByQuoteStatus(effectiveStatus)}>
                    {getBadgeLabelByQuoteStatus(effectiveStatus)}
                </BadgeCmp>
            )}

            {isLoading && !code && (
                <div className="flex h-6 w-20 border justify-center items-center border-gray300 rounded-lg gap-1 custom-loading">
                    <span className="rounded-full p-1 bg-warning500"></span>
                    <span className="text-xs">{t('QuotingHub.QuotingRequest.To_Quote')}</span>
                </div>
            )}
        </div>
    );
};

export default SharedQuoteHeader;
